export type VehicleBodyType = 'sedan' | 'pickup' | 'suv' | 'hatchback' | 'van';

export const VEHICLE_BODY_TYPES: VehicleBodyType[] = ['sedan', 'pickup', 'suv', 'hatchback', 'van'];

type PartLabel = { default: string } & Partial<Record<VehicleBodyType, string>>;

const PART_LABELS: Record<string, PartLabel> = {
  bumper_front: { default: 'Parachoques delantero' },
  bumper_rear: { default: 'Parachoques trasero' },
  hood: { default: 'Capó' },
  roof: { default: 'Techo' },
  trunk: { default: 'Maletero', pickup: 'Tolva', suv: 'Portalón', hatchback: 'Portalón', van: 'Puertas traseras' },
  door_fl: { default: 'Puerta delantera izquierda' },
  door_fr: { default: 'Puerta delantera derecha' },
  door_rl: { default: 'Puerta trasera izquierda' },
  door_rr: { default: 'Puerta trasera derecha' },
  fender_fl: { default: 'Guardabarros delantero izquierdo' },
  fender_fr: { default: 'Guardabarros delantero derecho' },
  fender_rl: { default: 'Guardabarros trasero izquierdo' },
  fender_rr: { default: 'Guardabarros trasero derecho' },
  wheel_fl: { default: 'Rueda delantera izquierda' },
  wheel_fr: { default: 'Rueda delantera derecha' },
  wheel_rl: { default: 'Rueda trasera izquierda' },
  wheel_rr: { default: 'Rueda trasera derecha' },
  mirror_left: { default: 'Espejo izquierdo' },
  mirror_right: { default: 'Espejo derecho' },
  glass_front: { default: 'Parabrisas' },
  glass_rear: { default: 'Luneta' },
  glass_fl: { default: 'Vidrio puerta delantera izquierda' },
  glass_fr: { default: 'Vidrio puerta delantera derecha' },
  glass_rl: { default: 'Vidrio puerta trasera izquierda' },
  glass_rr: { default: 'Vidrio puerta trasera derecha' },
  glass_ql: { default: 'Vidrio de custodia izquierdo' },
  glass_qr: { default: 'Vidrio de custodia derecho' },
  headlight_left: { default: 'Óptico izquierdo' },
  headlight_right: { default: 'Óptico derecho' },
  taillight_left: { default: 'Faro trasero izquierdo' },
  taillight_right: { default: 'Faro trasero derecho' }
};

export function normalizeVehicleType(value: string | null | undefined): VehicleBodyType {
  return VEHICLE_BODY_TYPES.includes(value as VehicleBodyType) ? (value as VehicleBodyType) : 'sedan';
}

export function canonicalPartId(part: string): string {
  if (!part) return part;
  if (PART_LABELS[part]) return part;
  const key = part.toLowerCase().replace(/[^a-z0-9]/g, '');
  const rule = MESH_RULES.find(item => key.includes(item.includes));
  return rule?.id ?? part;
}

export function formatVehiclePartLabel(part: string, vehicleType?: string): string {
  if (!part) return '';
  const id = canonicalPartId(part);
  const type = normalizeVehicleType(vehicleType);
  const known = PART_LABELS[id];
  if (known) return known[type] ?? known.default;
  if (_isTechnicalId(part)) return 'Pieza registrada';
  return part.replace(/_/g, ' ');
}

export function formatVehiclePartList(parts: unknown, vehicleType?: string): string {
  const ids = _asPartIds(parts);
  if (!ids.length) return '';
  return ids.map(id => formatVehiclePartLabel(id, vehicleType)).join(', ');
}

function _asPartIds(parts: unknown): string[] {
  if (Array.isArray(parts)) return parts.filter((p): p is string => typeof p === 'string' && !!p);
  if (typeof parts === 'string') {
    try {
      const parsed = JSON.parse(parts || '[]');
      return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

function _isTechnicalId(part: string): boolean {
  return part.length > 18 || /mesh|carpaint|poly|xt:|nissan_|mazda_|colorado/i.test(part) || /^\d+$/.test(part);
}

const MESH_RULES: { includes: string; id: string }[] = [
  { includes: 'leftdoor', id: 'door_fl' },
  { includes: 'rightdoor', id: 'door_fr' },
  { includes: 'doorfl', id: 'door_fl' },
  { includes: 'doorfr', id: 'door_fr' },
  { includes: 'doorbl', id: 'door_rl' },
  { includes: 'doorbr', id: 'door_rr' },
  { includes: 'houmen1', id: 'door_rl' },
  { includes: 'houmen3', id: 'door_rr' },
  { includes: 'doorl4', id: 'door_fl' },
  { includes: 'doorr4', id: 'door_fr' },
  { includes: 'skdoorfl', id: 'door_fl' },
  { includes: 'skdoorfr', id: 'door_fr' },
  { includes: 'skdoorbl', id: 'door_rl' },
  { includes: 'skdoorbr', id: 'door_rr' },
  { includes: 'hood', id: 'hood' },
  { includes: 'trunk', id: 'trunk' },
  { includes: 'boot6', id: 'trunk' },
  { includes: 'houbeigan', id: 'trunk' },
  { includes: 'frontbamper', id: 'bumper_front' },
  { includes: 'rearbamper', id: 'bumper_rear' },
  { includes: 'bumperf', id: 'bumper_front' },
  { includes: 'bumperr', id: 'bumper_rear' },
  { includes: 'baoxiangang', id: 'bumper_front' },
  { includes: 'baowei', id: 'bumper_rear' },
  { includes: 'smbumperf', id: 'bumper_front' },
  { includes: 'smbumperb', id: 'bumper_rear' },
  { includes: 'houshijingl', id: 'mirror_left' },
  { includes: 'houshijingr', id: 'mirror_right' },
  { includes: 'fenderfl', id: 'fender_fl' },
  { includes: 'fenderfr', id: 'fender_fr' },
  { includes: 'frontfender', id: 'fender_fl' },
  { includes: 'rearfender', id: 'fender_rl' },
  { includes: 'smfenderf', id: 'fender_fl' },
  { includes: 'smfenderb', id: 'fender_rl' },
  { includes: 'headlightl', id: 'headlight_left' },
  { includes: 'headlightr', id: 'headlight_right' },
  { includes: 'taillightl', id: 'taillight_left' },
  { includes: 'taillightr', id: 'taillight_right' },
  { includes: 'dadeng', id: 'headlight_left' },
  { includes: 'weideng', id: 'taillight_left' },
  { includes: 'boli', id: 'glass_front' },
  { includes: 'glasssidefrontleft', id: 'glass_fl' },
  { includes: 'glasssidefrontright', id: 'glass_fr' },
  { includes: 'glasssiderearleft', id: 'glass_rl' },
  { includes: 'glasssiderearright', id: 'glass_rr' },
  { includes: 'dingmesh', id: 'roof' },
  { includes: 'roof', id: 'roof' }
];
